import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../utils/api";

const STORAGE_KEY = "skin";
export const SKINS = ["klasican", "kristal"];
const BODY_CLASS = "skin-kristal";

const SkinContext = createContext(null);
const NO_SKIN = { skin: "klasican", setSkin: () => {}, syncFromServer: async () => {} };

function readLocal() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return SKINS.includes(v) ? v : "klasican";
  } catch {
    return "klasican"; // localStorage unavailable (private mode): the default look
  }
}

function writeLocal(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

/**
 * Optional skin. The choice is kept in localStorage (instant, also before login) and in the user's profile on the
 * server (follows the user to other devices). The class on <body> is also set by a tiny script in index.html
 * before the first paint, so the old look never flashes.
 */
export function SkinProvider({ children }) {
  const [skin, setSkinState] = useState(readLocal);

  useEffect(() => {
    document.body.classList.toggle(BODY_CLASS, skin === "kristal");
  }, [skin]);

  const setSkin = useCallback((value) => {
    if (!SKINS.includes(value)) return;
    setSkinState(value);
    writeLocal(value);
    // Best effort: when the profile column does not exist yet, or the user is not logged in, localStorage still works.
    fetch(`${API_BASE_URL}/skin.php`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ skin: value }),
    }).catch(() => {});
  }, []);

  // After login: take the choice saved in the profile (if there is one).
  const syncFromServer = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/skin.php`, { credentials: "include" });
      const json = await res.json();
      if (json?.success && SKINS.includes(json.skin)) {
        setSkinState(json.skin);
        writeLocal(json.skin);
      }
    } catch {
      // keep the local choice
    }
  }, []);

  const value = useMemo(() => ({ skin, setSkin, syncFromServer }), [skin, setSkin, syncFromServer]);
  return <SkinContext.Provider value={value}>{children}</SkinContext.Provider>;
}

export function useSkin() {
  // Without a provider (e.g. an isolated test) the app simply stays classic.
  return useContext(SkinContext) || NO_SKIN;
}
